/**
 * The rules the vNeuron engine ran on one procedure code, turned into cards the rule-detail
 * modal can render. A port of the Angular `MedicalService`.
 *
 * Pure functions only - no class, no singleton, no DI. The Angular original reached into a
 * shared data service for the claim's document list; here the documents a rule sentence names
 * are read off that rule's own conditionFound rows, so nothing in this module knows about the
 * app around it.
 *
 * `content` on every card and statement is an HTML STRING, rendered by the caller with
 * `dangerouslySetInnerHTML`. That is deliberate: the only markup in those strings is markup
 * this module emitted, because every interpolated value goes through `escapeHtml` first.
 * React does NOT escape inside `dangerouslySetInnerHTML` - do not drop those calls.
 */

/**
 * @typedef {Object} RuleData
 * @property {string}  title            'Diagnosis Rule' | 'Document Rule' | 'Clinical Terms Rule' | 'Procedure Rule' | 'Evidence Rule'
 * @property {string}  [operator]
 * @property {string}  [content]        HTML STRING - rendered with dangerouslySetInnerHTML
 * @property {RuleStatement[]} [statements]
 * @property {boolean|null} conditionResult
 * @property {boolean} expanded
 * @property {string}  [entityName]
 * @property {string}  [color]
 * @property {boolean} [hasNestedRules]
 * @property {boolean} [isProtocolView]
 */

/**
 * @typedef {Object} RuleStatement
 * @property {number|string} id
 * @property {string}  content           HTML STRING
 * @property {boolean|null} conditionResult
 * @property {boolean} expanded
 * @property {string}  [entityName]
 * @property {string}  [operator]
 * @property {string}  [booleanOperator]
 */

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

/** At most this many values are printed before the rest collapse into "+N values". */
export const MAX_VISIBLE = 3;

const RULE_NOT_EXECUTED = 'Rule not executed due to insufficient information provided.';

/** Shown on the diagnosis / procedure / evidence card when the engine sent no rule for it. */
const NO_DIAGNOSIS_RULE = 'Rule could not be executed with the provided information';
const NO_PROCEDURE_RULE = 'Rule could not be executed with the provided information';
const NO_EVIDENCE_RULE = 'There is no evidence rule for this procedure.';

const isEmptyRule = (rule) => !rule || (typeof rule === 'object' && Object.keys(rule).length === 0);

const colorByResult = (r) => (r === true ? '#4caf50' : r === false ? '#f44336' : '#9e9e9e');

const escapeHtml = (t) => (!t ? '' : String(t)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;'));

const OPERATOR_TEXT = {
    GREATER_THAN: 'greater than',
    GREATER_THAN_OR_EQUAL_TO: 'greater than or equal to',
    LESS_THAN: 'less than',
    LESS_THAN_OR_EQUAL_TO: 'less than or equal to',
    EQUAL_TO: 'equal to',
    NOT_EQUAL_TO: 'not equal to',
};

/** 'GREATER_THAN' -> 'greater than'; anything unmapped is just de-underscored. */
export function operatorText(operator) {
    if (!operator) {
        return '';
    }
    return OPERATOR_TEXT[operator] || String(operator).toLowerCase().replace(/_/g, ' ');
}

/** 'VALUE_GREATER_THAN_EQUAL_TO' -> 'GREATER THAN OR EQUAL TO'. */
export function formatOperator(op) {
    switch (op) {
        case 'VALUE_GREATER_THAN_EQUAL_TO':
            return 'GREATER THAN OR EQUAL TO';
        case 'VALUE_GREATER_THAN':
            return 'GREATER THAN';
        case 'VALUE_LESS_THAN_EQUAL_TO':
            return 'LESS THAN OR EQUAL TO';
        case 'VALUE_LESS_THAN':
            return 'LESS THAN';
        case 'VALUE_EQUAL_TO':
            return 'EQUAL TO';
        default:
            return String(op || '').replace(/_/g, ' ');
    }
}

/**
 * 'Name - 12, 14 mg (ICD code - X)'. The first letter is capitalised and every part is
 * dropped when it is absent, so a bare name comes back as just the name.
 */
export function displayName(data) {
    if (!data) {
        return '';
    }
    const raw = data.name || data.standardName || '';
    const name = raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : '';

    const values = Array.isArray(data.values)
        ? data.values.filter((v) => v !== null && v !== undefined && v !== '').join(', ')
        : (data.value === null || data.value === undefined ? '' : String(data.value));
    const measure = [values, data.unit || ''].filter(Boolean).join(' ').trim();

    const icd = data.icdcode || data.icdCode || '';

    let out = name;
    if (measure) {
        out = out ? `${out} - ${measure}` : measure;
    }
    if (icd) {
        out = out ? `${out} (ICD code - ${icd})` : `(ICD code - ${icd})`;
    }
    return out.trim();
}

/**
 * The "first three, then +N values" markup every builder needs.
 *
 * The Angular original open-codes this five times over; it lives in one place here and the
 * builders call it. `getClass` decides the highlight colour per item.
 *
 * @param {Array} items
 * @param {(item: any) => string} getName
 * @param {(item: any) => string} [getClass]
 * @returns {{visible: string, plus: string}} both HTML strings, `plus` already space-prefixed
 */
export function buildHighlightList(items, getName, getClass) {
    const list = Array.isArray(items) ? items : [];
    const className = getClass || (() => 'mark-text-highlight');

    const visible = list
        .slice(0, MAX_VISIBLE)
        .map((item) => `<span class="${className(item)}">${escapeHtml(getName(item))}</span>`)
        .join(' ');

    let plus = '';
    if (list.length > MAX_VISIBLE) {
        const rest = list.slice(MAX_VISIBLE).map((item) => getName(item)).join(', ');
        plus = ` <span class="plus-values-hover" title="${escapeHtml(rest)}"`
            + ` data-toggle="tooltip" data-placement="top">+${list.length - MAX_VISIBLE} values</span>`;
    }
    return {visible, plus};
}

/**
 * The documents the engine actually found this rule's values in, read off its conditionFound
 * rows - not the protocol's document list, which names what was asked for. A rule that found
 * nothing (or carries no conditionFound at all) has no documents to name.
 */
function foundDocumentNames(rule) {
    const names = (Array.isArray(rule && rule.conditionFound) ? rule.conditionFound : [])
        .filter((condition) => condition && condition.found)
        .map((condition) => condition.Found_in || condition.foundIn || condition.documentType)
        .filter(Boolean);
    return [...new Set(names)];
}

/** The document half of a rule sentence - the same in every builder that prints one. */
function buildDocumentNameList(rule) {
    const names = foundDocumentNames(rule);
    if (names.length === 0) {
        return '<span class="mark-text-highlight error">NO DOCUMENT FOUND</span>';
    }
    const {visible, plus} = buildHighlightList(
        names,
        (name) => String(name).toUpperCase(),
        () => 'mark-text-highlight success',
    );
    return `${visible}${plus}`;
}

/**
 * A grey card standing in for a rule the engine never ran, carrying the evidence remark.
 * The remark is engine text headed for dangerouslySetInnerHTML, so it is escaped.
 */
const placeholderCard = (title, entityName, content) => ({
    title,
    content: escapeHtml(content),
    conditionResult: null,
    expanded: true,
    entityName,
    color: '#9e9e9e',
});

/* ------------------------------------------------------------------ *
 * Entry point
 * ------------------------------------------------------------------ */

/**
 * @param {object} jsonData  the adjudication detail object for one procedure code
 * @param {'simple'|'protocol'} viewType
 * @returns {RuleData[]}
 */
export function getRuleData(jsonData, viewType = 'simple') {
    if (!jsonData) {
        return [];
    }
    return viewType === 'protocol'
        ? getProtocolViewRules(jsonData)
        : getSimpleRuleView(jsonData);
}

/* ------------------------------------------------------------------ *
 * Simple view
 * ------------------------------------------------------------------ */

/**
 * The four rules, in this order. A rule object that is there gets built. A missing
 * diagnosis / clinical-terms / procedure rule (null, undefined or `{}`) becomes a grey
 * placeholder with a fixed "no rule" message; a `null` document rule becomes a placeholder
 * carrying the document row's remark, and an `undefined` one produces nothing.
 */
export function getSimpleRuleView(jsonData) {
    const rules = [];

    const diagnosisRule = jsonData.diagnosisRule;
    if (!isEmptyRule(diagnosisRule)) {
        rules.push(buildDiagnosisRule(diagnosisRule));
    } else {
        rules.push(placeholderCard('Diagnosis Rule', 'diagnosis', NO_DIAGNOSIS_RULE));
    }

    const documentRule = jsonData.documentRule;
    if (documentRule && Object.keys(documentRule).length > 0) {
        rules.push(buildDocumentRule({...documentRule, rulesRemarks: jsonData.document?.[0]?.remarks}));
    } else if (documentRule === null) {
        // GUARD: jsonData.document[0] was indexed unguarded in the original.
        rules.push(placeholderCard('Document Rule', 'document', jsonData.document?.[0]?.remarks));
    }

    const clinicalTermsRule = jsonData.clinicalTermsRule;
    if (!isEmptyRule(clinicalTermsRule)) {
        const built = buildEvidenceRule({...clinicalTermsRule, rulesRemarks: jsonData.evidence?.[0]?.remarks});
        if (built) {
            rules.push(built);
        }
    } else {
        rules.push(placeholderCard('Clinical Terms Rule', 'evidence', NO_EVIDENCE_RULE));
    }

    const procedureRule = jsonData.procedureRule;
    if (!isEmptyRule(procedureRule)) {
        rules.push(buildProcedureRule({...procedureRule, rulesRemarks: jsonData.procedures?.[0]?.remarks}));
    } else {
        rules.push(placeholderCard('Procedure Rule', 'procedure', NO_PROCEDURE_RULE));
    }

    return rules;
}

/* ------------------------------------------------------------------ *
 * Builders
 * ------------------------------------------------------------------ */

/** @returns {RuleData} */
export function buildDiagnosisRule(rule) {
    const operator = rule.operator || 'ANY_OF_N_VALUES_IS_PRESENT';
    const conditionResult = rule.adjudicationResult ?? null;

    if (!rule.valuesToFind || rule.valuesToFind.length === 0) {
        return {
            title: 'Diagnosis Rule',
            operator,
            content: RULE_NOT_EXECUTED,
            conditionResult,
            expanded: true,
            entityName: 'diagnoses',
            color: colorByResult(conditionResult),
        };
    }

    const {visible, plus} = buildHighlightList(
        rule.valuesToFind,
        (value) => (value.name && value.name.toUpperCase()) || 'UNNAMED',
    );
    const foundDocuments = buildDocumentNameList(rule);

    return {
        title: 'Diagnosis Rule',
        operator,
        content: `Any of ${escapeHtml(rule.numberOfValues || 1)} diagnoses: ${visible}${plus}`
            + ` should be present in the documents: ${foundDocuments}`,
        conditionResult,
        expanded: true,
        entityName: 'diagnoses',
        color: colorByResult(conditionResult),
    };
}

/**
 * The diagnosis builder's twin, with one behavioural difference and one fix.
 *
 * @returns {RuleData}
 */
export function buildProcedureRule(rule) {
    const operator = rule.operator || 'ANY_OF_N_VALUES_IS_PRESENT';
    const conditionResult = rule.adjudicationResult ?? null;

    if (!rule.valuesToFind || rule.valuesToFind.length === 0) {
        // The original returned early ONLY when adjudicationResult was null - with an empty
        // valuesToFind and a non-null result it fell through and crashed on `.slice`.
        // FIX: both cases land on the placeholder card, which is what the early return was
        // already producing for the other half of the input space.
        return {
            title: 'Procedure Rule',
            operator,
            // The original early return printed the remark as-is; the fallback sentence is
            // only for the fall-through case it used to crash on.
            content: conditionResult === null
                ? escapeHtml(rule.rulesRemarks)
                : (escapeHtml(rule.rulesRemarks) || RULE_NOT_EXECUTED),
            conditionResult,
            expanded: true,
            entityName: 'procedures',
            color: colorByResult(conditionResult),
        };
    }

    const {visible, plus} = buildHighlightList(
        rule.valuesToFind,
        (value) => (value.name && value.name.toUpperCase()) || 'UNNAMED',
    );
    const foundDocuments = buildDocumentNameList(rule);

    return {
        title: 'Procedure Rule',
        operator,
        content: `Any of ${escapeHtml(rule.numberOfValues || 1)} procedure(s): ${visible}${plus}`
            + ` should be present in the documents: ${foundDocuments}`,
        conditionResult,
        expanded: true,
        entityName: 'procedures',
        color: colorByResult(conditionResult),
    };
}

/**
 * Two payload generations land here: the old one nests the values under `statements[]`, the
 * new one puts a single set of values on the rule itself.
 *
 * @returns {RuleData}
 */
export function buildDocumentRule(rule) {
    const conditions = [];

    if (Array.isArray(rule.statements)) {
        // Old format.
        rule.statements.forEach((stmt, statementIndex) => {
            if (!stmt || !stmt.valuesToFind) {
                return;
            }
            stmt.valuesToFind.forEach((value) => {
                conditions.push({
                    name: value.name,
                    found: stmt.conditionFound?.some((cf) => cf.name === value.name && cf.found) ?? false,
                    operator: stmt.operator,
                    numberOfValues: stmt.numberOfValues,
                    statementIndex,
                });
            });
        });
    } else if (Array.isArray(rule.valuesToFind)) {
        // New format.
        rule.valuesToFind.forEach((value) => {
            conditions.push({
                name: value.name,
                found: rule.conditionFound?.find((cf) => cf.name === value.name)?.found ?? false,
                operator: rule.operator,
                numberOfValues: rule.numberOfValues || 1,
                statementIndex: 0,
            });
        });
    }

    if (conditions.length === 0) {
        return {
            ...placeholderCard('Document Rule', 'document', rule.rulesRemarks),
            content: escapeHtml(rule.rulesRemarks) || RULE_NOT_EXECUTED,
        };
    }

    const operator = rule.booleanOperator || rule.operator || 'OR';

    const grouped = new Map();
    conditions.forEach((condition) => {
        if (!grouped.has(condition.statementIndex)) {
            grouped.set(condition.statementIndex, []);
        }
        grouped.get(condition.statementIndex).push(condition);
    });

    const statements = [];
    grouped.forEach((group, statementIndex) => {
        // Old format reads the operator off its own statement; new format off the rule.
        const stmt = Array.isArray(rule.statements) ? rule.statements[statementIndex] : rule;
        const {visible, plus} = buildHighlightList(
            group,
            (condition) => (condition.name && condition.name.toUpperCase()) || 'UNNAMED',
            (condition) => (condition.found ? 'mark-text-highlight success' : 'mark-text-highlight'),
        );

        let content;
        if (stmt?.operator === 'ALL_OF_THE_VALUES_ARE_PRESENT') {
            content = `All of document: ${visible}${plus} should be present.`;
        } else if (stmt?.operator === 'ANY_OF_N_VALUES_IS_PRESENT') {
            content = `Any of ${escapeHtml(stmt.numberOfValues || 1)} document: ${visible}${plus} should be present.`;
        } else {
            content = `Document: ${visible}${plus} should be present.`;
        }

        statements.push({
            id: statements.length + 1,
            content,
            conditionResult: null,
            expanded: true,
            operator,
        });
    });

    return {
        title: 'Document Rule',
        operator,
        content: operator === 'AND'
            ? 'All of the statements should be true'
            : 'Any of the statements should be true',
        statements,
        conditionResult: rule.adjudicationResult ?? null,
        expanded: true,
        entityName: 'document',
        hasNestedRules: true,
    };
}

/**
 * The clinical-terms rule arrives in five different shapes depending on how the engine
 * phrased it. They are tested in this order and the first match wins.
 *
 * @returns {RuleData|null}
 */
export function buildEvidenceRule(rule) {
    if (!rule) {
        return null;
    }

    // 1. A plain list of what was found.
    if (Array.isArray(rule)) {
        const items = rule.map((item) => {
            const name = item.name || item.standardName || RULE_NOT_EXECUTED;
            const className = item.found ? 'mark-text-highlight success' : 'mark-text-highlight error';
            return `<span class="${className}">${escapeHtml(name)}</span>`;
        });
        return {
            title: 'Clinical Terms Rule',
            content: `Evidence items: ${items.join(', ')}`,
            conditionResult: null,
            expanded: true,
            entityName: 'evidence',
        };
    }

    // 2. A single statement stated on the rule itself.
    if (rule.operator && rule.entityName && rule.valuesToFind) {
        const statement = {
            id: 1,
            content: buildEvidenceStatementContent(rule),
            conditionResult: rule.conditionFound?.[0]?.found ?? false,
            expanded: true,
            entityName: rule.entityName,
            operator: rule.operator,
        };
        return {
            title: 'Clinical Terms Rule',
            operator: rule.operator || 'ANY_OF_N_VALUES_IS_PRESENT',
            statements: [statement],
            // Deliberately null even though the statement carries a result: the card's own
            // border stays neutral and only the statement is coloured.
            conditionResult: null,
            expanded: true,
            entityName: 'evidence',
            hasNestedRules: true,
        };
    }

    // 3. Several statements.
    if (Array.isArray(rule.statements)) {
        const statements = rule.statements.map((stmt, index) => ({
            id: index + 1,
            content: buildEvidenceStatementContent(stmt),
            // GUARD: the original read stmt.conditionFound[0].found behind a check that the
            // array merely exists, and threw whenever it was there but empty.
            conditionResult: stmt?.conditionFound?.[0]?.found ?? false,
            expanded: true,
            entityName: stmt?.entityName,
            operator: stmt?.operator,
            booleanOperator: stmt?.booleanOperator,
        }));
        return {
            title: 'Clinical Terms Rule',
            operator: rule.booleanOperator || 'AND',
            statements,
            conditionResult: null,
            expanded: true,
            entityName: 'evidence',
            hasNestedRules: true,
        };
    }

    // 4. A bare comparison.
    if (rule.valueToCompare) {
        return {
            title: 'Clinical Terms Rule',
            content: `${escapeHtml(rule.valueToCompare?.name || 'VALUE')} is`
                + ` ${escapeHtml(formatOperator(rule.operator || ''))} ${escapeHtml(String(rule.value ?? ''))}`,
            conditionResult: null,
            expanded: true,
            entityName: rule.entityName || 'clinicalattribute',
        };
    }

    // 5. Nothing recognisable - show the engine's own remark.
    return {
        title: 'Clinical Terms Rule',
        content: escapeHtml(rule.rulesRemarks),
        conditionResult: null,
        expanded: true,
        entityName: 'evidence',
    };
}

/**
 * One evidence statement as a sentence.
 *
 * @param {object} statement
 * @returns {string} HTML
 */
export function buildEvidenceStatementContent(statement) {
    if (!statement) {
        return `<span class="mark-text-highlight error"> ${RULE_NOT_EXECUTED} </span>`;
    }

    if (statement.entityName === 'evidence') {
        if (!statement.valuesToFind || statement.valuesToFind.length === 0) {
            return `<span class="mark-text-highlight error"> ${RULE_NOT_EXECUTED} </span>`;
        }
        const {visible, plus} = buildHighlightList(
            statement.valuesToFind,
            (value) => (value.name && value.name.toUpperCase()) || 'UNNAMED',
            (value) => (statement.conditionFound?.some((cf) => cf.name === value.name && cf.found)
                ? 'mark-text-highlight success'
                : 'mark-text-highlight'),
        );
        // The original hard-coded this document list to a literal X-RAY / ULTRASOUND /
        // "+5 values" string left over from a screenshot, so every claim printed the same
        // two documents. It names the documents the evidence was found in here.
        const foundDocuments = buildDocumentNameList(statement);
        return `Any of ${escapeHtml(statement.numberOfValues || 1)} evidence: ${visible}${plus}`
            + ` should be present in the documents: ${foundDocuments}`;
    }

    const operator = statement.operator || '';
    const isComparison = operator.includes('GREATER') || operator.includes('LESS') || operator.includes('EQUAL');

    if (isComparison || operator === 'VALUE_IN') {
        const standardName = statement.conditionFound?.[0]?.standardName
            || statement.valuesToFind?.[0]?.name
            || statement.valueToCompare?.name
            || 'Unknown';
        const value = statement.value || statement.valueToCompare || 'N/A';
        // A comparison spells the operator out; VALUE_IN keeps it raw. VALUE_IN contains
        // none of GREATER/LESS/EQUAL, so it does reach this branch - the order is the
        // original's and matters.
        const verb = isComparison ? operatorText(operator) : operator;
        return `<span class="mark-text-highlight">${escapeHtml(standardName)}</span>`
            + ` should be ${escapeHtml(verb)}`
            + ` <span class="mark-text-highlight">${escapeHtml(displayValue(value))}</span>`;
    }

    return `Statement with operator: ${escapeHtml(statement.operator || 'UNKNOWN')}`;
}

/** `value` can be a scalar or a valueToCompare object; both have to print as text. */
function displayValue(value) {
    if (value === null || value === undefined) {
        return 'N/A';
    }
    if (typeof value === 'object') {
        return displayName(value) || value.value || 'N/A';
    }
    return String(value);
}

/* ------------------------------------------------------------------ *
 * Protocol view
 *
 * Unreached from the Medical Admissibility screen, which always asks for the simple view.
 * Ported so a caller that wants the protocol phrasing has it.
 * ------------------------------------------------------------------ */

/** @returns {RuleData[]} */
export function getProtocolViewRules(jsonData) {
    const rules = [];

    if (jsonData.diagnosisRule) {
        rules.push(buildProtocolDiagnosisRule(jsonData.diagnosisRule));
    }
    if (jsonData.documentRule) {
        const built = buildProtocolDocumentRule(jsonData.documentRule);
        if (built) {
            rules.push(built);
        }
    }
    if (jsonData.clinicalTermsRule) {
        const built = buildProtocolEvidenceRule(jsonData.clinicalTermsRule);
        if (built) {
            rules.push(built);
        }
    }
    if (jsonData.procedureRule) {
        rules.push(buildProcedureRule({...jsonData.procedureRule, rulesRemarks: jsonData.procedures?.[0]?.remarks}));
    }

    return rules;
}

/**
 * Unlike the simple view this one names the documents the engine actually found the values
 * in, joins the values with ', ' rather than a space, and prints all of them - no cap at 3.
 *
 * @returns {RuleData}
 */
export function buildProtocolDiagnosisRule(rule) {
    const conditionResult = rule.adjudicationResult ?? null;

    const values = (rule.valuesToFind || [])
        .map((value) => `<span class="mark-text-highlight">${escapeHtml((value.name && value.name.toUpperCase()) || 'UNNAMED')}</span>`)
        .join(', ');

    const documents = (rule.conditionFound || [])
        .filter((condition) => condition && condition.found)
        .map((condition) => {
            const name = condition.Found_in || condition.documentType || 'DISCHARGE SUMMARY';
            return `<span class="mark-text-highlight success">${escapeHtml(String(name).toUpperCase())}</span>`;
        })
        .join(', ');

    return {
        title: 'Diagnosis Rule',
        operator: rule.operator || 'ANY_OF_N_VALUES_IS_PRESENT',
        content: `Any of ${escapeHtml(rule.numberOfValues || 1)} diagnoses: ${values}`
            + ` should be present in the documents: ${documents || '<span class="mark-text-highlight error">NO DOCUMENT FOUND</span>'}`,
        conditionResult,
        expanded: true,
        entityName: 'diagnoses',
        color: colorByResult(conditionResult),
        isProtocolView: true,
    };
}

/** @returns {RuleData|null} - null, not a placeholder card, when there is nothing to say. */
export function buildProtocolDocumentRule(documentRule) {
    const conditions = [];

    if (Array.isArray(documentRule.statements)) {
        documentRule.statements.forEach((stmt, statementIndex) => {
            if (!stmt || !stmt.valuesToFind) {
                return;
            }
            stmt.valuesToFind.forEach((value) => {
                conditions.push({
                    name: value.name,
                    found: stmt.conditionFound?.some((cf) => cf.name === value.name && cf.found) ?? false,
                    statementIndex,
                });
            });
        });
    } else if (Array.isArray(documentRule.valuesToFind)) {
        documentRule.valuesToFind.forEach((value) => {
            conditions.push({
                name: value.name,
                found: documentRule.conditionFound?.find((cf) => cf.name === value.name)?.found ?? false,
                statementIndex: 0,
            });
        });
    }

    if (conditions.length === 0) {
        return null;
    }

    const operator = documentRule.booleanOperator || documentRule.operator || 'OR';

    const grouped = new Map();
    conditions.forEach((condition) => {
        if (!grouped.has(condition.statementIndex)) {
            grouped.set(condition.statementIndex, []);
        }
        grouped.get(condition.statementIndex).push(condition);
    });

    const statements = [];
    grouped.forEach((group, statementIndex) => {
        // GUARD: documentRule.statements[stmtIndex] was indexed unguarded in the original,
        // which throws for the new format, where `statements` does not exist at all.
        const stmt = documentRule.statements?.[statementIndex];
        const values = group
            .map((condition) => {
                const className = condition.found ? 'mark-text-highlight success' : 'mark-text-highlight';
                return `<span class="${className}">${escapeHtml((condition.name && condition.name.toUpperCase()) || 'UNNAMED')}</span>`;
            })
            .join(', ');

        let content;
        if (stmt?.operator === 'ALL_OF_THE_VALUES_ARE_PRESENT') {
            content = `All of document: ${values} should be present.`;
        } else if (stmt?.operator === 'ANY_OF_N_VALUES_IS_PRESENT') {
            content = `Any of ${escapeHtml(stmt.numberOfValues || 1)} document: ${values} should be present.`;
        } else {
            content = `Document: ${values} should be present.`;
        }

        statements.push({
            id: statements.length + 1,
            content,
            conditionResult: null,
            expanded: true,
            operator,
        });
    });

    return {
        title: 'Document Rule',
        operator,
        content: operator === 'AND'
            ? 'All of the statements should be true'
            : 'Any of the statements should be true',
        statements,
        conditionResult: documentRule.adjudicationResult ?? null,
        expanded: true,
        entityName: 'document',
        hasNestedRules: true,
        isProtocolView: true,
    };
}

/** Same statements as the simple view, under the protocol view's own title. */
export function buildProtocolEvidenceRule(rule) {
    const built = buildEvidenceRule(rule);
    if (!built) {
        return null;
    }
    return {...built, title: 'Evidence Rule', isProtocolView: true};
}
